import { Injectable } from "@angular/core";
import { AccountAppCommand, AccountAppValue, RewardStateFilterType } from "@fabric-types/app-account";
import { Store, select } from "@ngrx/store";
import { WelcomeBonusSelectionFlag } from "@obg/mat.ui.common/customer-verification/src/customer-verification.model";
import { AuthSelector, CoreState, CustomerManagementSelector } from "@obg/ngx.core";
import { DocumentRef, FeatureFlag, FeatureFlagService } from "@obg/ngx.global";
import { isEmpty } from "lodash-es";
import { Observable, combineLatest, of, queueScheduler } from "rxjs";
import { filter, map, mergeMap, observeOn, switchMap, take } from "rxjs/operators";

import { CustomerOfferAction } from "../campaign/customer-offer/customer-offer.action";
import { CustomerOfferSelector } from "../campaign/customer-offer/customer-offer.selector";

import { RegisterRewardsPayload, VerificationType } from "./customer-verification.model";

export const WELCOME_BONUS_SELECTION_FLAG_KEY = "welcome_bonus_selection";

@Injectable({
	providedIn: "root",
})
export class CustomerVerificationService {
	private _welcomeBonusSelectionFlag: WelcomeBonusSelectionFlag | undefined;
	isRewardsEnabled = true;

	constructor(
		private store: Store<CoreState>,
		private customerOfferSelector: CustomerOfferSelector,
		private customerOfferAction: CustomerOfferAction,
		private featureFlagService: FeatureFlagService,
		private documentRef: DocumentRef
	) {
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.GetRewardsEnabled, {
			onSuccess: (isEnabled: boolean) => (this.isRewardsEnabled = isEnabled),
		});
	}

	initialize() {
		this.store.dispatch(this.customerOfferAction.get());
	}

	getAvailableRewards$(): Observable<RegisterRewardsPayload> {
		const customerData$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(filter((data) => !!data));
		if (this.isRewardsEnabled) {
			const depositRewards$ = new Observable(
				this.documentRef.obgEventBus.retrieve(AccountAppValue.GetDepositRewards, {
					rewardFilter: RewardStateFilterType.Claimable,
				})
			).pipe(map((depositRewards) => !isEmpty(depositRewards)));

			const campaignOffers$ = this.store.pipe(
				select(this.customerOfferSelector.isEnabled()),
				mergeMap((isEnabled) => {
					if (!isEnabled) {
						return of(false);
					}

					return of(undefined).pipe(
						// dispatching of actions is asyncronous. queueScheduler is needed to wait till isBusy is set up correctly
						observeOn(queueScheduler),
						switchMap(() => this.store.pipe(select(this.customerOfferSelector.isBusy()))),
						filter((isBusy) => !isBusy),
						switchMap(() => this.store.pipe(select(this.customerOfferSelector.hasOffers())))
					);
				})
			);

			return combineLatest([customerData$, depositRewards$, campaignOffers$]).pipe(
				filter(([customerData]) => !!customerData),
				take(1),
				map(([customerData, hasDepositRewards, hasCampaignOffers]) => ({
					customerData: customerData!,
					verificationType:
						hasDepositRewards || hasCampaignOffers
							? VerificationType.withRewards
							: VerificationType.created,
				}))
			);
		}

		const isFirstLogin$ = this.store
			.select(AuthSelector.isFirstLogin)
			.pipe(filter((isFirstLogin) => !!isFirstLogin));

		return combineLatest([isFirstLogin$, customerData$]).pipe(
			filter(([, customerData]) => !!customerData),
			take(1),
			map(([, customerData]) => ({
				customerData: customerData!,
				verificationType: VerificationType.created,
			}))
		);
	}

	private getWelcomeBonusSelectionFlag(): FeatureFlag<WelcomeBonusSelectionFlag | undefined> | undefined {
		return this.featureFlagService.get<WelcomeBonusSelectionFlag | undefined>(WELCOME_BONUS_SELECTION_FLAG_KEY);
	}

	welcomeBonusSelectionFlag(): boolean | undefined {
		this._welcomeBonusSelectionFlag = this.getWelcomeBonusSelectionFlag();
		return this._welcomeBonusSelectionFlag?.enabled;
	}
}
